import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import './MyWorksGrid.css';

const PAGE_SIZE = 9;

const LABELS = {
  th: { reviews: 'รีวิว', events: 'อีเวนต์', venues: 'สถานที่', promotions: 'โปรโมชั่น',
        comingSoon: 'เร็ว ๆ นี้', comingSoonText: 'ฟีเจอร์โปรโมชั่นกำลังจะมาเร็ว ๆ นี้',
        empty: 'ยังไม่มีรายการ', view: 'ดู', edit: 'แก้ไข', delete: 'ลบ', manage: 'จัดการ',
        confirmDelete: 'ยืนยันการลบรายการนี้?' },
  en: { reviews: 'Reviews', events: 'Events', venues: 'Venues', promotions: 'Promotions',
        comingSoon: 'Coming Soon', comingSoonText: 'Promotions are coming soon',
        empty: 'No items yet', view: 'View', edit: 'Edit', delete: 'Delete', manage: 'Manage',
        confirmDelete: 'Delete this item?' },
  zh: { reviews: '评论', events: '活动', venues: '场所', promotions: '优惠',
        comingSoon: '即将推出', comingSoonText: '优惠功能即将上线',
        empty: '暂无内容', view: '查看', edit: '编辑', delete: '删除', manage: '管理',
        confirmDelete: '确认删除此项？' }
};

const formatCount = (n) => {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
};

// ---- per-type field helpers ----
const reviewImage = (r) => r.thumbnail_url || r.main_image_url || '';
const reviewTitle = (r, lang) => {
  if (lang === 'en') return r.place_name_en || r.place_name || '';
  if (lang === 'zh') return r.place_name_cn || r.place_name || '';
  return r.place_name_th || r.place_name || '';
};

const eventImage = (e) => e.event_image_link || '';
const eventTitle = (e) => e.event_name || '';

const venueImage = (v) =>
  v.thumbnail_url || v.generated_thumbnail_url || v.image_url ||
  v.main_image_url || v.logo_url || v.cover_image_url || v.photo_url || '';
const venueTitle = (v) =>
  v.venue_name || v.name || v.business_name || v.title || '';

// De-duplicate multilingual event rows that share a master_event_id
const dedupeEvents = (events, lang) => {
  const dbLang = lang === 'en' ? 'EN' : lang === 'zh' ? 'CN' : 'TH';
  const groups = new Map();
  events.forEach((e) => {
    const key = e.master_event_id || e.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  });
  return Array.from(groups.values()).map((rows) => {
    return rows.find((r) => r.language === dbLang) || rows[0];
  });
};

const MyWorksGrid = ({
  reviews = [],
  events = [],
  venues = [],
  language = 'th',
  onOpen,       // (type, item)
  onEdit,       // (type, item)
  onDelete,     // (type, item)
  onActiveTypeChange, // (type) — report the selected category tab upward
  view = 'grid', // 'grid' shows the tiles; 'map' shows only the category tabs
}) => {
  const l = LABELS[language] || LABELS.th;

  const [activeType, setActiveType] = useState('reviews');

  // Let the parent mirror the selected category (e.g. to show its map markers).
  useEffect(() => {
    if (onActiveTypeChange) onActiveTypeChange(activeType);
  }, [activeType, onActiveTypeChange]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  const sentinelRef = useRef(null);

  // Normalize the active tab's items into a common shape
  const items = useMemo(() => {
    switch (activeType) {
      case 'reviews':
        return reviews.map((r) => ({
          key: `r-${r.id}`, type: 'review', raw: r,
          image: reviewImage(r), title: reviewTitle(r, language),
          views: r.view_count, badgeIcon: <RateReviewOutlinedIcon />, badgeLabel: l.reviews,
        }));
      case 'events':
        return dedupeEvents(events, language).map((e) => ({
          key: `e-${e.master_event_id || e.id}`, type: 'event', raw: e,
          image: eventImage(e), title: eventTitle(e),
          views: e.view_count, badgeIcon: <EventOutlinedIcon />, badgeLabel: l.events,
        }));
      case 'venues':
        return venues.map((v) => ({
          key: `v-${v.id}`, type: 'venue', raw: v,
          image: venueImage(v), title: venueTitle(v),
          views: v.view_count, badgeIcon: <StorefrontOutlinedIcon />, badgeLabel: l.venues,
        }));
      default:
        return [];
    }
  }, [activeType, reviews, events, venues, language, l]);

  // Reset pagination whenever the tab or its data changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeType, items.length]);

  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  // Infinite scroll: load the next 9 when the sentinel becomes visible
  useEffect(() => {
    if (!hasMore) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, items.length));
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, items.length, activeType]);

  const tabs = [
    { key: 'reviews', label: l.reviews, icon: <RateReviewOutlinedIcon />, count: reviews.length },
    { key: 'events', label: l.events, icon: <EventOutlinedIcon />, count: dedupeEvents(events, language).length },
    { key: 'venues', label: l.venues, icon: <StorefrontOutlinedIcon />, count: venues.length },
    { key: 'promotions', label: l.promotions, icon: <LocalOfferOutlinedIcon />, count: null },
  ];

  const openMenu = useCallback((e, item) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuItem(item);
  }, []);
  const closeMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuItem(null);
  }, []);

  const handleTileClick = useCallback((item) => {
    onOpen && onOpen(item.type, item.raw);
  }, [onOpen]);

  const handleEdit = useCallback(() => {
    if (menuItem) onEdit && onEdit(menuItem.type, menuItem.raw);
    closeMenu();
  }, [menuItem, onEdit, closeMenu]);

  const handleView = useCallback(() => {
    if (menuItem) onOpen && onOpen(menuItem.type, menuItem.raw);
    closeMenu();
  }, [menuItem, onOpen, closeMenu]);

  const handleDelete = useCallback(() => {
    if (menuItem && window.confirm(l.confirmDelete)) {
      onDelete && onDelete(menuItem.type, menuItem.raw);
    }
    closeMenu();
  }, [menuItem, onDelete, closeMenu, l]);

  const renderGridBody = () => {
    if (items.length === 0) {
      return (
        <div className="myworks-empty">
          <InboxOutlinedIcon className="myworks-empty-icon" />
          <span>{l.empty}</span>
        </div>
      );
    }
    return (
      <>
        <div className="myworks-grid">
          {visibleItems.map((item) => (
            <div
              key={item.key}
              className="myworks-tile"
              onClick={() => handleTileClick(item)}
              role="button"
              tabIndex={0}
            >
              <span className="myworks-badge">{item.badgeIcon}{item.badgeLabel}</span>

              <IconButton
                size="small"
                className="myworks-actionbtn"
                onClick={(e) => openMenu(e, item)}
                aria-label="actions"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>

              {item.image ? (
                <img src={item.image} alt={item.title || ''} loading="lazy" />
              ) : (
                <div className="myworks-tile-placeholder">
                  <span className="ph-icon">{item.badgeIcon}</span>
                  <span className="ph-title">{item.title || item.badgeLabel}</span>
                </div>
              )}

              {item.image && item.title ? (
                <span className="myworks-caption">{item.title}</span>
              ) : null}

              {typeof item.views === 'number' && item.views > 0 ? (
                <span className="myworks-views">
                  <VisibilityIcon />{formatCount(item.views)}
                </span>
              ) : null}
            </div>
          ))}
        </div>

        {hasMore && (
          <>
            <div ref={sentinelRef} className="myworks-sentinel" />
            <div className="myworks-loading">…</div>
          </>
        )}
      </>
    );
  };

  const mapView = view === 'map';

  return (
    <div
      className="myworks-root"
      style={mapView ? {
        pointerEvents: 'auto',
        background: 'rgba(249,250,251,0.92)',
        backdropFilter: 'blur(6px)',
        borderBottom: '1px solid rgba(17,24,39,0.06)',
      } : undefined}
    >
      <div className="myworks-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`myworks-chip ${activeType === tab.key ? 'active' : ''}`}
            onClick={() => setActiveType(tab.key)}
            type="button"
          >
            <span className="myworks-chip-icon">{tab.icon}</span>
            {tab.label}
            {tab.count != null && <span className="myworks-chip-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* In map view only the category tabs are shown; the tiles are hidden so
          the map (with this category's markers) stays visible underneath. */}
      {mapView ? null : activeType === 'promotions' ? (
        <div className="myworks-comingsoon">
          <HourglassEmptyIcon className="cs-icon" />
          <span className="cs-badge">{l.comingSoon}</span>
          <span className="cs-text">{l.comingSoonText}</span>
        </div>
      ) : (
        renderGridBody()
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuItem && menuItem.type === 'venue' ? (
          <MenuItem onClick={handleView}>
            <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{l.manage}</ListItemText>
          </MenuItem>
        ) : (
          [
            <MenuItem key="view" onClick={handleView}>
              <ListItemIcon><VisibilityOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{l.view}</ListItemText>
            </MenuItem>,
            <MenuItem key="edit" onClick={handleEdit}>
              <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{l.edit}</ListItemText>
            </MenuItem>,
            <MenuItem key="delete" onClick={handleDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>{l.delete}</ListItemText>
            </MenuItem>,
          ]
        )}
      </Menu>
    </div>
  );
};

export default MyWorksGrid;
